import type { CourseDocument, HoleDocument } from './CourseDocument';

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}
const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;
const isNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

function validateHole(hole: unknown, index: number, errors: string[]): hole is HoleDocument {
  if (!isRecord(hole)) {
    errors.push(`holes[${index}] must be an object`);
    return false;
  }
  for (const key of ['id', 'name'])
    if (typeof hole[key] !== 'string') errors.push(`holes[${index}].${key} must be a string`);
  for (const key of ['number', 'par', 'yardage', 'activePinIndex'])
    if (!isNumber(hole[key])) errors.push(`holes[${index}].${key} must be a number`);
  for (const key of [
    'teeBoxes',
    'pins',
    'fairways',
    'greens',
    'roughRegions',
    'bunkers',
    'waterRegions',
    'vegetation',
    'props',
  ])
    if (!Array.isArray(hole[key])) errors.push(`holes[${index}].${key} must be an array`);
  return true;
}

export function validateCourseDocument(value: unknown): ValidationResult {
  const errors: string[] = [];
  if (!isRecord(value)) return { valid: false, errors: ['Course document must be an object'] };
  if (value.schemaVersion !== 1 && value.schemaVersion !== 2)
    errors.push('schemaVersion must be 1 or 2');
  for (const key of ['id', 'name', 'description', 'environmentPreset'])
    if (typeof value[key] !== 'string') errors.push(`${key} must be a string`);
  if (!Array.isArray(value.holes)) errors.push('holes must be an array');
  else value.holes.forEach((hole, index) => validateHole(hole, index, errors));
  return { valid: errors.length === 0, errors };
}

export function parseCourseDocumentJson(json: string): {
  document?: CourseDocument;
  errors: string[];
} {
  try {
    const parsed: unknown = JSON.parse(json);
    const result = validateCourseDocument(parsed);
    if (!result.valid) return { errors: result.errors };
    const doc = parsed as CourseDocument;
    if ((doc as { schemaVersion: number }).schemaVersion === 1)
      (doc as { schemaVersion: number }).schemaVersion = 2;
    return { document: doc, errors: [] };
  } catch (err) {
    return { errors: [`Invalid JSON: ${err instanceof Error ? err.message : String(err)}`] };
  }
}
